import React, {useState} from 'react';
import {Spinner} from 'reactstrap';
import {
    AlertTriangle,
    BookOpen,
    Check,
    ChevronDown,
    ChevronRight,
    Code2,
    Files,
    Terminal,
    X
} from 'lucide-react';
import CodeBlock from '../../../components/utility/markdown/CodeBlock';
import {ToolCallItem, ToolProgressEntry} from '../../api/types';
import {ArtifactStrip} from '../ArtifactViews';
import MarkdownMessage from '../MarkdownMessage';

const NO_FILES = 'No files.';

const readArgument = (argumentsJson: string, name: string): string => {
    try {
        const value = JSON.parse(argumentsJson)?.[name];
        return typeof value === 'string' ? value : '';
    } catch {
        return '';
    }
};

const describe = (item: ToolCallItem) => {
    switch (item.toolName) {
        case 'runtime_run_python':
            return {title: 'Python code', icon: <Code2 size={14} />};
        case 'runtime_run_shell':
            return {title: 'Shell command', icon: <Terminal size={14} />};
        case 'runtime_read_skill': {
            const name = readArgument(item.argumentsJson, 'name');
            return {
                title: name ? `Read skill: ${name}` : 'Read skill',
                icon: <BookOpen size={14} />
            };
        }
        case 'runtime_list_files':
            return {title: 'Files', icon: <Files size={14} />};
        default:
            return null;
    }
};

const ProgressLine: React.FC<{entry: ToolProgressEntry}> = ({entry}) => {
    const className =
        entry.level === 'error'
            ? 'text-danger'
            : entry.level === 'warning'
              ? 'text-warning'
              : 'text-muted';

    return (
        <div className={`d-flex align-items-center gap-1 ${className}`}>
            {entry.level === 'success' && <Check size={12} />}
            {entry.level === 'warning' && <AlertTriangle size={12} />}
            {entry.level === 'error' && <X size={12} />}
            <span>{entry.text}</span>
        </div>
    );
};

const OutputSection: React.FC<{
    label: string;
    text?: string | null;
    isError?: boolean;
}> = ({label, text, isError}) =>
    text ? (
        <>
            <div className="fw-bold mt-2">{label}</div>
            <pre
                className={`mb-0 bg-white border rounded p-2 ${isError ? 'text-danger' : ''}`}
                style={{
                    maxHeight: 240,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap'
                }}
            >
                {text}
            </pre>
        </>
    ) : null;

/**
 * A code runtime tool call. Collapsed by default - the progress lines already
 * tell the story - with two things that are never hidden: errors, and the files
 * the call produced.
 */
const RuntimeToolCallView: React.FC<{item: ToolCallItem}> = ({item}) => {
    const [expanded, setExpanded] = useState(false);

    const description = describe(item);
    if (!description) {
        return null; // an unknown member of the family renders nothing
    }

    const isRunning = item.status === 'in_progress';
    const output = item.runtimeOutput;
    const isFailed =
        !isRunning &&
        (item.status === 'failed' ||
            !!item.error ||
            output?.status === 'failed');
    const notConfigured = output?.status === 'not_configured';
    const entries = item.progressEntries ?? [];

    // An empty listing is a one-line note, not a card
    if (
        item.toolName === 'runtime_list_files' &&
        !isRunning &&
        item.output?.trim() === NO_FILES
    ) {
        return (
            <div className="mb-2 small text-muted d-flex align-items-center gap-1">
                <Files size={12} />
                The secure runtime has no files yet.
            </div>
        );
    }

    const code =
        item.toolName === 'runtime_run_python'
            ? readArgument(item.argumentsJson, 'code')
            : item.toolName === 'runtime_run_shell'
              ? readArgument(item.argumentsJson, 'command')
              : '';

    // While running, show the live trail. Afterwards, only what went wrong.
    const visibleEntries =
        isRunning || expanded
            ? entries
            : entries.filter(e => e.level === 'error' || e.level === 'warning');

    return (
        <div
            className={`mb-2 border rounded p-2 small ${isFailed ? 'border-danger' : 'bg-light'}`}
        >
            <div className="d-flex align-items-center gap-2">
                {description.icon}
                <button
                    type="button"
                    className="btn btn-link btn-sm p-0 text-decoration-none d-flex align-items-center gap-1"
                    onClick={() => setExpanded(value => !value)}
                >
                    {expanded ? (
                        <ChevronDown size={14} />
                    ) : (
                        <ChevronRight size={14} />
                    )}
                    <span className={isFailed ? 'text-danger' : undefined}>
                        {description.title}
                        {isRunning && '…'}
                        {isFailed && ' (Failed)'}
                    </span>
                </button>
                {isRunning && <Spinner size="sm" />}
                {!isRunning && output && output.executionTimeMs > 0 && (
                    <span className="text-muted">
                        {(output.executionTimeMs / 1000).toFixed(1)}s
                    </span>
                )}
            </div>

            {visibleEntries.length > 0 && (
                <div className="mt-1">
                    {visibleEntries.map((entry, index) => (
                        <ProgressLine key={index} entry={entry} />
                    ))}
                </div>
            )}

            {notConfigured && (
                <div className="text-warning mt-1">
                    Code execution is not available in this environment.
                </div>
            )}

            {item.error && <div className="text-danger mt-1">{item.error}</div>}

            {!expanded && isFailed && output?.stderr && (
                <pre
                    className="mb-0 mt-1 text-danger"
                    style={{
                        maxHeight: 96,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap'
                    }}
                >
                    {output.stderr}
                </pre>
            )}

            {expanded && (
                <div className="mt-2">
                    {code && (
                        <CodeBlock
                            className={
                                item.toolName === 'runtime_run_python'
                                    ? 'language-python'
                                    : 'language-bash'
                            }
                        >
                            {code}
                        </CodeBlock>
                    )}

                    {output ? (
                        <>
                            <OutputSection
                                label="Output"
                                text={output.stdout}
                            />
                            <OutputSection
                                label="Errors"
                                text={output.stderr}
                                isError
                            />
                            <OutputSection
                                label="Result"
                                text={output.result}
                            />
                            {output.truncated && (
                                <div className="text-muted mt-1">
                                    The output was long; the assistant saw a
                                    shortened version.
                                </div>
                            )}
                        </>
                    ) : item.toolName === 'runtime_read_skill' &&
                      item.output ? (
                        <div
                            className="bg-white border rounded p-2"
                            style={{maxHeight: 320, overflow: 'auto'}}
                        >
                            <MarkdownMessage text={item.output} />
                        </div>
                    ) : (
                        <OutputSection label="Result" text={item.output} />
                    )}
                </div>
            )}

            <ArtifactStrip artifacts={item.artifacts ?? []} />
        </div>
    );
};

export default RuntimeToolCallView;
